// GSM 2026-08-25: quick add-to-cart with quantity steppers on product
// cards and in the add-to-cart drawer, plus the header cart-count badge.
//
// .gsm-card-add buttons are rendered by snippets/product-card.liquid and
// the client-built grids (sections/related-products.liquid,
// snippets/collection-search.liquid). The drawer's cart-items list is NOT
// one of them — its rows carry their own line-keyed steppers, managed by
// gsm-cart-drawer.js. While the button's variant is in the
// cart the button is swapped for a − / n / + stepper — the number only,
// matching the cart page (Dom's 2026-08-25 preview feedback, twice: the
// button must not revert to "Add to Cart" while the item sits in the
// cart, and quantity must be adjustable within the item's inventory from
// the card AND from the drawer popup).
//
// /cart.js is the source of truth. applyCart() repaints the badge and
// swaps every button ↔ stepper to match the cart; it runs on page load,
// after this file's own adds/changes, after jQuery cart calls (PDP form,
// cart-page qty edits, upsell modals — via ajaxComplete), and after the
// drawer's fetch()-based adds (gsm-cart-drawer.js refreshTotals calls
// applyCart). A MutationObserver applies cart state to buttons rendered
// after load (related grid, in-collection search results, the drawer).
//
// Inventory bound: data-max-qty (rendered when the variant tracks
// inventory with the deny policy) disables + at the cap, and seeds a
// per-variant registry so the drawer's stepper — whose button has no
// attribute — knows the cap too. Buttons with no known cap fall back to
// lazy discovery: a 422 from /cart/add.js marks the current quantity as
// the max.
window.GSMCartBadge = (function () {
  var lastCart = null;
  var caps = {}; // variant id -> known max quantity

  function paint(count) {
    var dots = document.querySelectorAll('.site-header__cart-indicator');
    var label = count > 99 ? '99+' : String(count);
    for (var i = 0; i < dots.length; i++) {
      dots[i].textContent = label;
      dots[i].classList.toggle('hide', count === 0);
    }
  }

  function cartQty(variantId) {
    if (!lastCart) return 0;
    var q = 0;
    for (var i = 0; i < lastCart.items.length; i++) {
      if (lastCart.items[i].variant_id === variantId) q += lastCart.items[i].quantity;
    }
    return q;
  }

  // The line − should decrement: the plain quick-added line first, ahead
  // of lines carrying properties (framing/diploma upsell extras).
  function cartLine(variantId) {
    if (!lastCart) return null;
    var fallback = null;
    for (var i = 0; i < lastCart.items.length; i++) {
      var it = lastCart.items[i];
      if (it.variant_id !== variantId) continue;
      var props = it.properties || {};
      var bare = true;
      for (var k in props) {
        if (Object.prototype.hasOwnProperty.call(props, k) && props[k]) { bare = false; break; }
      }
      if (bare) return it;
      if (!fallback) fallback = it;
    }
    return fallback;
  }

  function maxFor(btn, variantId) {
    var attr = parseInt(btn.getAttribute('data-max-qty'), 10);
    if (!isNaN(attr)) return attr;
    return caps[variantId] != null ? caps[variantId] : Infinity;
  }

  // A 422 from /cart/add.js is current truth — it overwrites; a rendered
  // attribute only seeds a cap nothing else has claimed.
  function noteMax(variantId, qty) {
    caps[variantId] = qty;
  }

  function seedCap(variantId, qty) {
    if (caps[variantId] == null) caps[variantId] = qty;
  }

  function buildStepper(btn, variantId) {
    var el = document.createElement('div');
    el.className = 'gsm-card-stepper ' + (btn.getAttribute('data-stepper-class') || 'product-card__atc');
    el.setAttribute('data-variant-id', variantId);
    el.innerHTML =
      '<button type="button" class="gsm-stepper__btn gsm-stepper__minus" aria-label="Remove one from cart">&minus;</button>' +
      '<span class="gsm-stepper__qty" aria-live="polite"></span>' +
      '<button type="button" class="gsm-stepper__btn gsm-stepper__plus" aria-label="Add one more to cart">+</button>';
    btn.insertAdjacentElement('afterend', el);
    return el;
  }

  function applyToButton(btn) {
    var vid = Number(btn.getAttribute('data-variant-id'));
    if (!vid) return;
    if (!btn.getAttribute('data-label')) btn.setAttribute('data-label', btn.textContent);
    var attr = parseInt(btn.getAttribute('data-max-qty'), 10);
    if (!isNaN(attr)) seedCap(vid, attr);
    var qty = cartQty(vid);
    var next = btn.nextElementSibling;
    var stepper = next && next.classList && next.classList.contains('gsm-card-stepper') ? next : null;
    if (qty > 0) {
      if (!stepper) stepper = buildStepper(btn, vid);
      btn.hidden = true;
      stepper.hidden = false;
      stepper.querySelector('.gsm-stepper__qty').innerHTML =
        qty + '<span class="visually-hidden"> in cart</span>';
      var plus = stepper.querySelector('.gsm-stepper__plus');
      var atMax = qty >= maxFor(btn, vid);
      plus.disabled = atMax;
      plus.title = atMax ? 'No more available' : '';
      stepper.querySelector('.gsm-stepper__minus').disabled = false;
    } else {
      if (stepper) stepper.hidden = true;
      btn.hidden = false;
    }
    // Reset the button for its next appearance either way.
    btn.disabled = false;
    btn.classList.remove('is-added');
    btn.textContent = btn.getAttribute('data-label');
  }

  function applyCart(cart) {
    if (!cart || !cart.items) return cart;
    lastCart = cart;
    paint(cart.item_count);
    var btns = document.querySelectorAll('.gsm-card-add');
    for (var i = 0; i < btns.length; i++) applyToButton(btns[i]);
    // Nudge other open tabs (a watched cart page re-syncs on this; its
    // fingerprint check makes no-change nudges free).
    if (typeof BroadcastChannel !== 'undefined') {
      try {
        (window.__gsmCartBC = window.__gsmCartBC || new BroadcastChannel('gsm-cart'))
          .postMessage({ total: cart.total_price });
      } catch (err) { /* ignore */ }
    }
    return cart;
  }

  function sync() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(applyCart)
      .catch(function () { return null; });
  }

  function bind() {
    if (window.jQuery) {
      window.jQuery(document).ajaxComplete(function (evt, xhr, settings) {
        if (!settings || !settings.url) return;
        if (/\/cart\/(add|change|update)/.test(settings.url)) sync();
      });
    }
    // Buttons rendered after load (related grid, in-collection search,
    // the drawer's items list) get cart state as they appear.
    if (window.MutationObserver) {
      new MutationObserver(function (muts) {
        if (!lastCart) return;
        for (var i = 0; i < muts.length; i++) {
          var added = muts[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            var n = added[j];
            if (!n || n.nodeType !== 1) continue;
            if (n.classList && n.classList.contains('gsm-card-add')) {
              applyToButton(n);
            } else if (n.querySelectorAll) {
              var btns = n.querySelectorAll('.gsm-card-add');
              for (var k = 0; k < btns.length; k++) applyToButton(btns[k]);
            }
          }
        }
      }).observe(document.body, { childList: true, subtree: true });
    }
    sync();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind);
  } else {
    bind();
  }

  return {
    paint: paint,
    sync: sync,
    applyCart: applyCart,
    cartQty: cartQty,
    cartLine: cartLine,
    noteMax: noteMax
  };
})();

(function () {
  if (window.__gsmQuickAddBound) return;
  window.__gsmQuickAddBound = true;

  var badge = window.GSMCartBadge;

  function bumpCartIcon() {
    var carts = document.querySelectorAll('.site-header__cart');
    for (var i = 0; i < carts.length; i++) {
      carts[i].classList.remove('gsm-cart-bump');
      void carts[i].offsetWidth; // restart the animation
      carts[i].classList.add('gsm-cart-bump');
    }
  }

  function notifyError(message) {
    if (window.theme && window.theme.Notify) window.theme.Notify.open('error', message, true);
  }

  // While the drawer is open its subtotal must track +/− too;
  // refreshTotals re-applies cart state to every stepper on its own.
  function refreshUi(cart) {
    if (window.GSMCartDrawer && document.body.classList.contains('gsm-drawer-open')) {
      window.GSMCartDrawer.refreshTotals();
    } else if (cart) {
      badge.applyCart(cart);
    } else {
      badge.sync();
    }
  }

  function addToCart(variantId) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 })
    }).then(function (r) {
      return r.json().then(function (data) {
        if (!r.ok) {
          var err = new Error((data && (data.description || data.message)) || 'Could not add to cart');
          err.status = r.status;
          throw err;
        }
        return data;
      });
    });
  }

  // First add: swap to the stepper and open the drawer, like a PDP add.
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest ? e.target.closest('.gsm-card-add') : null;
    if (!btn || btn.disabled) return;
    e.preventDefault();
    var vid = Number(btn.getAttribute('data-variant-id'));
    if (!vid) return;
    if (!btn.getAttribute('data-label')) btn.setAttribute('data-label', btn.textContent);
    btn.disabled = true;
    btn.textContent = 'Adding…';
    addToCart(vid)
      .then(function (item) {
        btn.textContent = '✓ Added';
        btn.classList.add('is-added');
        bumpCartIcon();
        // In-drawer re-add: keep the open drawer; otherwise open it.
        if (document.body.classList.contains('gsm-drawer-open')) {
          refreshUi(null);
          return;
        }
        badge.sync(); // swaps the button for the stepper
        if (window.GSMCartDrawer && window.GSMCartDrawer.openAfterAdd(item)) return;
        if (window.theme && window.theme.Notify) window.theme.Notify.open('success', false, true);
      })
      .catch(function (err) {
        btn.disabled = false;
        btn.textContent = btn.getAttribute('data-label');
        notifyError(err && err.message);
        badge.sync(); // someone may have bought the last one — re-check state
      });
  });

  // Stepper + / − (cards and drawer alike). State repaints from the cart,
  // which also re-enables the controls.
  document.addEventListener('click', function (e) {
    var ctl = e.target && e.target.closest ? e.target.closest('.gsm-stepper__btn') : null;
    if (!ctl || ctl.disabled) return;
    var stepper = ctl.closest('.gsm-card-stepper');
    if (!stepper) return;
    e.preventDefault();
    var vid = Number(stepper.getAttribute('data-variant-id'));
    if (!vid) return;
    var buttons = stepper.querySelectorAll('.gsm-stepper__btn');
    for (var i = 0; i < buttons.length; i++) buttons[i].disabled = true;

    if (ctl.classList.contains('gsm-stepper__plus')) {
      var before = badge.cartQty(vid);
      addToCart(vid)
        .then(function () {
          bumpCartIcon();
          refreshUi(null);
        })
        .catch(function (err) {
          if (err && err.status === 422) {
            // Shopify refused: everything in stock is already in the cart.
            badge.noteMax(vid, before);
          } else {
            notifyError(err && err.message);
          }
          refreshUi(null);
        });
    } else {
      var line = badge.cartLine(vid);
      if (!line) {
        refreshUi(null);
        return;
      }
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: line.key, quantity: line.quantity - 1 })
      })
        .then(function (r) {
          if (!r.ok) throw new Error('Could not update cart');
          return r.json();
        })
        .then(function (cart) {
          refreshUi(cart);
        })
        .catch(function (err) {
          notifyError(err && err.message);
          refreshUi(null);
        });
    }
  });
})();
